import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { Container, Form, Spinner, Alert, Button, InputGroup } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import LinkTaskModal from '../components/LinkTaskModal';
import EditTaskModal from '../components/EditTaskModal';
import guestRequestApi from '../services/guestRequestApi';
import taskApi from '../services/taskApi';
import secureApiService from '../services/secureApi';
import { formatDate, formatRelativeTime } from '../utils/dateFormatter';
import {
  GR_TYPE_LABELS,
  GR_STATUS_LABELS,
  GR_PRIORITY_LABELS
} from '../config/guestRequestLabels';
import '../styles/BookingGuestRequestsSection.css';
import '../styles/GuestRequestsHubPage.css';

// Guest Requests Hub V1.1 — global open-requests triage queue.
// Spec: tasks/grh-v1.1-aggregation.md
//
// READ-ONLY surface. Editing flows back to:
//   - Booking page (for un-linked GRs — uses BookingGuestRequestsSection)
//   - /tasks page (for linked GRs — Task is the execution authority)
//
// Per Phase 1 i18n strategy, GRH territory is Arabic-only (no t() calls).

const GuestRequestsHubPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // TurnKey owners monitor service delivery, not operations — they get a read-only
  // view with simplified statuses and no internal task (Create/linked-Task) mechanics.
  // Core staff only (AppSecure /guest-requests; the reads are core staff since owner app 8c batch 3). The remaining
  // `isTurnKeyOwner` gate is dead and goes with the legacy owner-branch cleanup (tasks/legacy-axios-cleanup.md).
  const isTurnKeyOwner = user?.role === 'turnKeyOwner';
  const statusLabels = GR_STATUS_LABELS;

  const [requests, setRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Client-side filters (server only honors propertyId — priority + search apply in-memory)
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Link-Task modal state (escalation: GR → new Task)
  const [linkModalRequest, setLinkModalRequest] = useState(null);

  // Edit-Task modal state (in-place edit of the linked Task — Task #4)
  const [editingTask, setEditingTask] = useState(null);
  const [loadingTaskBadgeId, setLoadingTaskBadgeId] = useState(null);
  const [taskLoadError, setTaskLoadError] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = propertyFilter && propertyFilter !== 'all'
        ? { propertyId: propertyFilter }
        : {};
      const data = await guestRequestApi.listOpen(params);
      setRequests(data.requests || []);
    } catch (err) {
      console.error('Failed to load open guest requests:', err);
      setError('تعذّر تحميل طلبات الضيوف. حاول لاحقًا.');
    } finally {
      setLoading(false);
    }
  }, [propertyFilter]);

  const fetchProperties = useCallback(async () => {
    try {
      const data = await secureApiService.getProperties();
      setProperties(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load properties:', err);
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Priority + text search applied client-side. Endpoint already returns
  // urgent-first sorted. Search matches against booking code, guest, property,
  // description, and Arabic type label — broad enough to be useful, narrow
  // enough that ops gets predictable hits.
  const filteredRequests = useMemo(() => {
    let result = requests;

    if (priorityFilter !== 'all') {
      result = result.filter(r => r.priority === priorityFilter);
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter(r => {
        const haystack = [
          r.displayBookingCode,
          r.displayGuestName,
          r.displayPropertyName,
          r.description,
          GR_TYPE_LABELS[r.type]
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }

    return result;
  }, [requests, priorityFilter, searchQuery]);

  const urgentCount = useMemo(
    () => requests.filter(r => r.priority === 'urgent').length,
    [requests]
  );

  const handleRowClick = (request) => {
    // Click row → navigate to booking page (where the GR detail + edit flow lives).
    if (request.bookingId?._id) {
      navigate(`/bookings/${request.bookingId._id}`);
    }
  };

  // Task #4 — open the existing Task edit modal in place. Preserves triage
  // flow continuity (no /tasks navigation). Fetches the full Task first
  // because EditTaskModal expects a complete task object, not just an ID.
  const handleLinkedTaskClick = async (request, e) => {
    e.stopPropagation();
    const taskId = request.taskId?._id || request.taskId;
    if (!taskId) return;

    // Mutually-exclusive modal guard — closing the LinkTaskModal here prevents
    // the two from rendering stacked if the user clicked Create-Task on one
    // row and then a linked-task badge on another before closing the first.
    setLinkModalRequest(null);

    setLoadingTaskBadgeId(request._id);
    setTaskLoadError(null);
    try {
      const resp = await taskApi.getTask(taskId);
      // Backend shape: { success: true, data: { task } } (be/routes/taskRoutes.js).
      const task = resp?.data?.task;
      if (task && task._id) {
        setEditingTask(task);
      } else {
        setTaskLoadError('تعذّر تحميل المهمة.');
      }
    } catch (err) {
      console.error('Failed to load Task for edit:', err);
      setTaskLoadError('تعذّر تحميل المهمة. حاول لاحقًا.');
    } finally {
      setLoadingTaskBadgeId(null);
    }
  };

  // After the Task is saved, the GR row's linkedTaskStatus may have changed
  // — and if the Task was completed, Task→GR sync may have flipped the GR
  // itself to 'completed' (which removes it from the open queue). Cheapest
  // path to consistency: refetch the list. Cost is one HTTP round-trip;
  // benefit is correctness without manual reconciliation logic.
  const handleTaskUpdated = useCallback(() => {
    setEditingTask(null);
    fetchRequests();
  }, [fetchRequests]);

  const handleCreateTaskClick = (request, e) => {
    e.stopPropagation();
    // Mutually-exclusive modal guard — see handleLinkedTaskClick.
    setEditingTask(null);
    setLinkModalRequest(request);
  };

  // Replace the row in state with the decorated GR returned by the backend.
  // This includes hasLinkedTask=true + linkedTaskTicketId + linkedTaskStatus,
  // so the row instantly switches from "Create Task" button to the linked-Task
  // badge without a refetch.
  const handleTaskLinked = useCallback((updatedRequest) => {
    setRequests(prev => prev.map(r => r._id === updatedRequest._id ? updatedRequest : r));
  }, []);

  return (
    <DesktopLayout activeNavItem="guest-requests" headerTitle="طلبات الضيوف">
      <Container fluid className="guest-requests-hub-page" dir="rtl">
        <div className="grh-page-header">
          <div className="grh-page-title">
            <i className="bi bi-inbox grh-page-title-icon"></i>
            <h2>طلبات الضيوف</h2>
            {!loading && requests.length > 0 && (
              <span className="grh-count-badge">
                {requests.length}
                {urgentCount > 0 && (
                  <span className="grh-urgent-pill">
                    {urgentCount} عاجل
                  </span>
                )}
              </span>
            )}
          </div>
          <p className="grh-page-subtitle">
            صندوق وارد الطلبات عبر جميع الحجوزات والعقارات. للتعديل، انتقل إلى صفحة الحجز أو المهمة المرتبطة.
          </p>
        </div>

        <div className="grh-search-bar">
          <InputGroup>
            <InputGroup.Text className="grh-search-icon">
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="ابحث برقم الحجز، الضيف، العقار، أو الوصف..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="grh-search-input"
              aria-label="البحث في الطلبات"
            />
            {searchQuery && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchQuery('')}
                className="grh-search-clear"
                aria-label="مسح البحث"
              >
                <i className="bi bi-x-lg"></i>
              </Button>
            )}
          </InputGroup>
        </div>

        <div className="grh-filters">
          <Form.Group className="grh-filter-group">
            <Form.Label className="grh-filter-label">العقار</Form.Label>
            <Form.Select
              value={propertyFilter}
              onChange={(e) => setPropertyFilter(e.target.value)}
              className="grh-filter-select"
            >
              <option value="all">جميع العقارات</option>
              {properties.map((p) => (
                <option key={p._id} value={p._id}>{p.name}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="grh-filter-group">
            <Form.Label className="grh-filter-label">الأولوية</Form.Label>
            <div className="grh-priority-chips">
              {[
                { key: 'all', label: 'الكل' },
                { key: 'urgent', label: 'عاجلة' },
                { key: 'normal', label: 'عادية' }
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  className={`grh-chip ${priorityFilter === opt.key ? 'grh-chip--active' : ''}`}
                  onClick={() => setPriorityFilter(opt.key)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Form.Group>

          <Button
            variant="link"
            size="sm"
            onClick={fetchRequests}
            className="grh-refresh-btn"
            disabled={loading}
          >
            <i className="bi bi-arrow-clockwise"></i> تحديث
          </Button>
        </div>

        {error && (
          <Alert variant="danger" className="grh-page-error">{error}</Alert>
        )}

        {taskLoadError && (
          <Alert
            variant="warning"
            className="grh-page-error"
            dismissible
            onClose={() => setTaskLoadError(null)}
          >
            {taskLoadError}
          </Alert>
        )}

        {loading ? (
          <div className="grs-loading">
            <Spinner animation="border" size="sm" />
            <span>جارٍ تحميل الطلبات...</span>
          </div>
        ) : error ? null : filteredRequests.length === 0 ? (
          <div className="grs-empty">
            <div className="grs-empty-icon">
              <i className="bi bi-inbox"></i>
            </div>
            <p>لا توجد طلبات مفتوحة</p>
            <p className="grs-empty-hint">
              {searchQuery || priorityFilter !== 'all' || propertyFilter !== 'all'
                ? 'جرّب تعديل البحث أو عوامل التصفية'
                : 'تظهر هنا الطلبات النشطة من جميع الحجوزات'}
            </p>
          </div>
        ) : (
          <div className="grs-list grh-list">
            {filteredRequests.map((req) => (
              <div
                key={req._id}
                className={`grs-row grh-row grs-row--${req.priority}`}
                onClick={() => handleRowClick(req)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(req); }}
              >
                <div className="grs-row__main">
                  <div className="grs-row__head">
                    <span className={`grs-type grs-type--${req.type}`}>
                      {GR_TYPE_LABELS[req.type] || req.type}
                    </span>
                    <span className={`grs-priority grs-priority--${req.priority}`}>
                      {GR_PRIORITY_LABELS[req.priority] || req.priority}
                    </span>
                    <span className={`grs-status-badge grs-status-badge--${req.status}`}>
                      {statusLabels[req.status] || req.status}
                    </span>
                  </div>

                  {req.description && (
                    <p className="grs-row__desc grh-row__desc">{req.description}</p>
                  )}

                  <div className="grh-row__meta">
                    {req.requestedForDate && (
                      <span className="grh-meta-item" title="موعد التنفيذ المطلوب من الضيف">
                        <i className="bi bi-calendar-event"></i>
                        موعد التنفيذ المطلوب: {formatDate(req.requestedForDate)}
                      </span>
                    )}
                    <span className="grh-meta-item">
                      <i className="bi bi-person"></i>
                      {req.displayGuestName || '—'}
                    </span>
                    {req.displayBookingCode && (
                      <span className="grh-meta-item grh-meta-item--booking">
                        <i className="bi bi-bookmark"></i>
                        {req.displayBookingCode}
                      </span>
                    )}
                    <span className="grh-meta-item">
                      <i className="bi bi-house"></i>
                      {req.displayPropertyName || '—'}
                    </span>
                    <span className="grh-meta-item">
                      <i className="bi bi-clock"></i>
                      {formatRelativeTime(req.createdAt)}
                    </span>
                  </div>
                </div>

                {!isTurnKeyOwner && (
                <div className="grh-row__actions">
                  {req.hasLinkedTask ? (
                    <button
                      type="button"
                      className="grh-linked-task-badge"
                      onClick={(e) => handleLinkedTaskClick(req, e)}
                      disabled={loadingTaskBadgeId === req._id}
                      title="فتح المهمة المرتبطة"
                    >
                      {loadingTaskBadgeId === req._id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <i className="bi bi-link-45deg"></i>
                      )}
                      <span className="grh-linked-task-id">
                        {req.linkedTaskTicketId || 'مهمة'}
                      </span>
                      <span className="grh-linked-task-status">
                        {GR_STATUS_LABELS[req.linkedTaskStatus] || req.linkedTaskStatus}
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="grh-create-task-btn"
                      onClick={(e) => handleCreateTaskClick(req, e)}
                      title="إنشاء مهمة ميدانية مرتبطة بهذا الطلب"
                    >
                      <i className="bi bi-plus-circle"></i>
                      إنشاء مهمة
                    </button>
                  )}
                </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Container>

      <LinkTaskModal
        show={!!linkModalRequest}
        request={linkModalRequest}
        onHide={() => setLinkModalRequest(null)}
        onLinked={handleTaskLinked}
      />

      <EditTaskModal
        show={!!editingTask}
        task={editingTask}
        onHide={() => setEditingTask(null)}
        onTaskUpdated={handleTaskUpdated}
      />
    </DesktopLayout>
  );
};

export default GuestRequestsHubPage;
